/**
 * 업무 프로젝트 칸반 보기 — 상태별 열(시안 F 보드: 4열 grid gap 9 · 열 r8 pad 8 6 6 · 열 순서 톤 · 카드 r7 pad 11 10) ·
 * 카드를 다른 열에 놓으면 상태를 바꾼다(이동 요청은 부모가 한다). 색·모양은 uiStyles.
 *
 * - 열은 받은 `statuses` 순서(= 프로젝트 선택지의 sort_order)대로, 카드는 받은 `tasks` 순서대로 그린다
 *   (정렬은 부모의 목록 조건이 정한다 — 같은 열 안에서 순서를 바꾸는 기능은 없다).
 * - 🔴 끄는 카드 id 는 상태로 든다 — 브라우저는 dragover 중 dataTransfer.getData() 를 못 읽는다.
 * - 쓰기 권한이 없거나(canEdit=false) 보관된 업무는 끌 수 없고, 열에 «업무 추가» 도 없다.
 */
import { useState } from 'react';
import { COLORS, TONES, badge, btn, card } from '../../utils/uiStyles';
// 열 톤·머리 알약 톤 — 홈 «내 업무» 보드와 같이 쓴다.
import { BOARD_PILL, BOARD_TONES } from './boardStyle';

// 반응형은 인라인으로 못 한다 — 판 앞에 한 번 둔다. 열 수(--gw-board-cols)는 판의 인라인 변수가 정한다.
// 🔴 align-items 를 넣지 않는다(stretch) — 열 높이가 줄마다 맞아야 톤 배경·놓기 영역이 끊기지 않는다.
const BOARD_CSS = '.gw-board{display:grid;grid-template-columns:repeat(var(--gw-board-cols,4),minmax(0,1fr));gap:9px}'
  + '@media (max-width:1200px){.gw-board{grid-template-columns:repeat(2,minmax(0,1fr))}}'
  + '@media (max-width:600px){.gw-board-card-title{font-size:13px!important}}';

const titleButton = {
  width: '100%', padding: 0, border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer',
  fontSize: '14px', fontWeight: 400, color: COLORS.text, lineHeight: 1.5, letterSpacing: '-0.3px', margin: '0 0 12px',
  // 제목은 두 줄까지만 보이고 넘치면 말줄임한다.
  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
};
const metaText = { fontSize: '10px', color: COLORS.textMuted };
// 담당자 19px 원(시안 .avatar) — 첫 담당자 이름 첫 글자.
const avatarStyle = {
  width: '19px', height: '19px', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  flexShrink: 0, fontSize: '9px', fontWeight: 600, background: TONES.gray.soft, color: COLORS.textSub,
};

/**
 * 카드 담당자 표시 — 2명까지 이름 + «외 N명»(지원보드 summarizeAssigneeNames 와 같은 규칙).
 * - 🔴 `assignee_names` 가 없는 응답(옛 서버)은 `assignee_name` 그대로 보인다.
 * - 이름이 없는(지워진) 사람은 서버의 이은 이름(`assignee_name`)처럼 뺀다.
 * - 🔴 업무 표(TaskPmPage)에 같은 함수가 하나 더 있다 — 그쪽이 여기서 import 하면 이 파일을 default 만 있는
 *   모의로 바꾸는 r6 시험들의 리스트가 깨진다. 규칙을 바꾸면 둘 다 바꾼다.
 * 돌려주는 값: { text: 보일 글자('' = 담당자 없음), title: 전체 이름,
 *              names: 앞 max명 이름, more: «외 N명»('' = 넘치지 않음) } — text = names + (more ? ' ' + more : '')
 */
function assigneeSummary(task, max = 2) {
  const names = Array.isArray(task?.assignee_names) ? task.assignee_names.filter(Boolean) : [];
  if (names.length === 0) {
    const single = task?.assignee_name || '';
    return { text: single, title: single, names: single, more: '' };
  }
  const overflow = names.length - max;
  const shown = names.slice(0, max).join(', ');
  const more = overflow > 0 ? `외 ${overflow}명` : '';
  return { text: more ? `${shown} ${more}` : shown, title: names.join(', '), names: shown, more };
}

export default function TaskPmKanban({ statuses, tasks, optionMap, canEdit, onOpenTask, onAddTask, onMove }) {
  const [dragId, setDragId] = useState(null);     // 지금 끌고 있는 업무 id
  const [overId, setOverId] = useState(null);     // 카드가 올라와 있는 열의 상태 id

  const rows = Array.isArray(tasks) ? tasks : [];
  const keyById = new Map(rows.map((t) => [t.id, t.key]));

  // 유형·우선순위 알약(부모 표의 renderOption 과 같은 모양) — 선택지가 없으면 그리지 않는다(줄표도 없다).
  const pill = (id, tone) => {
    const option = optionMap?.get(id);
    if (!option) return null;
    return <span style={{ ...badge(tone), background: `${option.color}22`, color: option.color, fontSize: '10px', padding: '1px 4px' }}>{option.label}</span>;
  };

  const startDrag = (task, draggable) => (e) => {
    if (!draggable) { e.preventDefault(); return; }
    e.dataTransfer.setData('text/plain', String(task.id));
    e.dataTransfer.effectAllowed = 'move';
    setDragId(task.id);
  };
  const endDrag = () => { setDragId(null); setOverId(null); };

  // 🔴 끄는 카드가 있을 때만 받는다 — 파일 끌어 놓기 같은 다른 끌기는 열에 반응하지 않는다.
  const overColumn = (status) => (e) => {
    if (dragId == null) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    if (overId !== status.id) setOverId(status.id);
  };
  // 자식 사이를 지날 때 테두리가 깜박이지 않게, 열 밖으로 나갔을 때만 끈다.
  const leaveColumn = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setOverId(null);
  };
  const dropOnColumn = (status) => (e) => {
    e.preventDefault();
    const id = dragId ?? Number(e.dataTransfer?.getData('text/plain'));
    setDragId(null);
    setOverId(null);
    const task = rows.find((t) => t.id === id);
    if (!task || String(task.status_id) === String(status.id)) return;   // 제자리에 놓으면 아무것도 하지 않는다
    onMove(task, status.id);
  };

  const statusList = statuses || [];
  // 열 수 = 상태 수(1~4) — 5개 이상이면 4열로 줄바꿈한다(가로 스크롤 없음). ≤1200 은 CSS 가 2열로 덮는다.
  const boardCols = Math.min(Math.max(statusList.length, 1), 4);

  return (
    <>
      <style>{BOARD_CSS}</style>
      <div role="region" aria-label="칸반 보기" className="gw-board" style={{ '--gw-board-cols': String(boardCols) }}>
        {statusList.map((status, i) => {
          const items = rows.filter((t) => String(t.status_id) === String(status.id));
          const over = overId === status.id;
          const tone = TONES[BOARD_PILL[i % 4]];
          // 🔴 jsdom 은 background 의 var() 값을 버린다 → 톤 순서는 data-board-tone 으로도 남긴다(시험용).
          return (
            <section key={status.id} aria-label={`${status.label} 열`} data-status-id={status.id} data-board-tone={i % 4}
              onDragOver={canEdit ? overColumn(status) : undefined}
              onDragLeave={canEdit ? leaveColumn : undefined}
              onDrop={canEdit ? dropOnColumn(status) : undefined}
              style={{
                boxSizing: 'border-box', minWidth: 0, padding: '8px 6px 6px',
                background: BOARD_TONES[i % 4], borderRadius: '8px',
                border: `1px solid ${over ? COLORS.primary : 'transparent'}`,
              }}>
              {/* 머리 — 알약(점 · 상태 이름) · 개수 · «+»(열 상태로 새 업무 창). minHeight 28 = «+»(아이콘 단추 28) 유무로 높이가 갈리지 않게 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: '0 1px 10px', minHeight: '28px' }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '1px 5px', borderRadius: '20px',
                  fontSize: '10px', lineHeight: '18px' /* 시안 .pill 18px */, fontWeight: 400, whiteSpace: 'nowrap',
                  background: tone.soft, color: tone.ink, minWidth: 0,
                }}>
                  <span aria-hidden="true" style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'currentColor', flexShrink: 0 }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{status.label}</span>
                </span>
                <span style={metaText}>{items.length}</span>
                {/* «+» = 시안 F 아이콘 단추(btn('icon') 28×28 · r5 · 흐림). hover 바탕은 공용 CSS(.gw-btn--icon · public/index.html)가 맡는다. */}
                {canEdit && (
                  <button type="button" aria-label={`${status.label}에 업무 추가`} onClick={() => onAddTask(status.id)}
                    className="gw-btn gw-btn--icon"
                    style={{
                      ...btn('icon'), boxSizing: 'border-box', padding: 0, fontSize: '16px', lineHeight: 1,
                      marginLeft: 'auto', flexShrink: 0,
                    }}>+</button>
                )}
              </div>

              {/* 빈 열에도 놓을 자리를 남긴다. */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minHeight: '40px' }}>
                {items.map((t) => {
                  const draggable = Boolean(canEdit && !t.archived_at);
                  const parentKey = t.parent_task_id && t.parent_task_id !== t.id ? keyById.get(t.parent_task_id) : null;
                  const typePill = pill(t.type_id, 'default');
                  const priorityPill = pill(t.priority_id, 'warn');
                  const assignee = assigneeSummary(t);
                  const initial = assignee.names.trim().charAt(0);   // 아바타 글자 — 첫 담당자 이름 첫 글자('' = 담당자 없음)
                  return (
                    <div key={t.id} data-testid={`kanban-card-${t.id}`} draggable={draggable}
                      onDragStart={startDrag(t, draggable)} onDragEnd={endDrag}
                      style={{
                        ...card, width: '100%', boxSizing: 'border-box', padding: '11px 10px', borderRadius: '7px',
                        border: `1px solid var(--gw-line, ${COLORS.borderSoft})`,
                        boxShadow: `0 1px 1px var(--gw-line, ${COLORS.borderSoft})`,
                        display: 'flex', flexDirection: 'column', gap: 0,
                        cursor: draggable ? 'grab' : 'default', opacity: dragId === t.id ? 0.5 : 1,
                      }}>
                      {/* 윗줄은 보일 것이 있을 때만 그린다(없으면 줄 자체가 없다 — 줄표도 넣지 않는다). */}
                      {(parentKey || typePill || priorityPill) && (
                        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px', marginBottom: '6px' }}>
                          {parentKey && <span style={{ fontSize: '11px', color: COLORS.textMuted }}>{`↳ ${parentKey}`}</span>}
                          {typePill}
                          {priorityPill}
                        </div>
                      )}
                      <button type="button" className="gw-board-card-title" onClick={() => onOpenTask(t.id)} style={titleButton}>{t.title}</button>
                      {/* 아랫줄 — 제목 margin 12 가 시안 푸터 margin-top 12 역할. minHeight 19 = 담당자 없는 카드도 같은 높이 */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px', marginTop: 0, minHeight: '19px' }}>
                        <span style={{ ...metaText, flexShrink: 0 }}>{t.key}</span>
                        {/* 🔴 space-between 이라 자식이 셋이면 아바타가 가운데로 간다 → 아바타 + 이름을 오른쪽 자식 하나로 묶는다. */}
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
                          {initial && <span aria-hidden="true" data-kanban-avatar style={avatarStyle}>{initial}</span>}
                          {/* 🔴 «외 N명» 은 줄이지 않고 이름 쪽만 말줄임한다(10-14 e2e: 한 덩어리로 자르면 «N명» 이 가려졌다).
                              넘치지 않으면(0~2명) 지금처럼 한 조각이다. 조각 사이 ' ' 는 flex 라 그려지지 않고 textContent 만 맞춘다. */}
                          <span title={assignee.title || undefined}
                            style={assignee.more
                              ? { ...metaText, display: 'inline-flex', minWidth: 0, gap: '4px' }
                              : { ...metaText, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {assignee.more ? (
                              <>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{assignee.names}</span>
                                {' '}
                                <span style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>{assignee.more}</span>
                              </>
                            ) : (assignee.text || '담당자 없음')}
                          </span>
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* 열 끝 «업무 추가» — 시안 .new-card(투명 11px 줄). 글자는 시안 «새 업무» 대신 그대로 둔다(접근성 이름 보존). */}
              {canEdit && (
                <button type="button" onClick={() => onAddTask(status.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '5px', width: '100%', boxSizing: 'border-box',
                    padding: '5px 5px 8px', marginTop: '2px', background: 'transparent', border: 'none',
                    fontSize: '11px', color: COLORS.textMuted, textAlign: 'left', cursor: 'pointer',
                  }}>
                  <span aria-hidden="true" style={{ fontSize: '13px', lineHeight: 1 }}>+</span>업무 추가
                </button>
              )}
            </section>
          );
        })}
      </div>
    </>
  );
}
